import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
	ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import {
	getBusinessTemplateStorageRefByDocument,
	getDocumentNonHashedId,
	getDocumentVersion,
	getTemplateVersionList,
} from '@core/util/documents';
import { createId } from '@core/util/file';
import { BusinessBaseDocx } from '@models/businesses';
import {
	BusinessDocumentVersionItem,
	GlobalWordDocumentTemplate,
} from '@models/documents';
import { MatterFileType } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { GlobalDocxTemplatesRepo } from '@repos/global-docx-templates/global-docx-templates.repo';
import {
	CopyTemplateParams,
	DocumentType,
	WordDocumentStyles,
} from '@services/docx-document/docx-document.models';
import { DocxDocumentService } from '@services/docx-document/docx-document.service';
import { FileNameService } from '@services/file/file-name.service';
import { UploadService } from '@services/file/upload.service';
import { WebdavService } from '@services/webdav/webdav.service';
import { BusinessState } from '@state/business';
import { cloneDeep } from 'lodash';
import { take } from 'rxjs/operators';
import { DropdownOption } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export enum EditDocumentDrawerModeEnum {
	CREATE = 'CREATE',
	EDIT = 'EDIT',
}
export interface EditDocumentdrawerData {
	document: GlobalWordDocumentTemplate;
	globalDocument: GlobalWordDocumentTemplate;
	baseDocxs: BusinessBaseDocx[];
	mode: EditDocumentDrawerModeEnum;
}
@Component({
	selector: 'app-edit-document-drawer',
	templateUrl: './edit-document-drawer.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class EditDocumentDrawerComponent implements OnInit {
	@ViewChild('form') form: NgForm;
	public fileTypes = MatterFileType;
	public document: GlobalWordDocumentTemplate;
	public globalDocument: GlobalWordDocumentTemplate;
	public versionList: DropdownOption<BusinessDocumentVersionItem>[] = [];
	public initialVersion: BusinessDocumentVersionItem;
	public baseDocxList: DropdownOption<string>[] = [
		{ name: 'None', value: null },
	];
	public hasVersionChanged = false;

	public EditDocumentDrawerModeEnum = EditDocumentDrawerModeEnum;
	public mode: EditDocumentDrawerModeEnum;

	@SelectSnapshot(BusinessState.getBusinessWordDocumentStyles)
	wordDocumentStyles: WordDocumentStyles;

	@SelectSnapshot(BusinessState.businessId) businessId: string;
	constructor(
		@Inject(DRAWER_DATA) private data: EditDocumentdrawerData,
		private drawerService: DrawerService,
		private globalDocxTemplatesRepo: GlobalDocxTemplatesRepo,
		private docxDocumentService: DocxDocumentService,
		private uploadService: UploadService,
		private webdavService: WebdavService,
		private fileNameService: FileNameService,
	) {
		this.document = cloneDeep(data.document);
		this.mode = data.mode;
		if (data.mode === EditDocumentDrawerModeEnum.CREATE) {
			const initiaTemplateVersionItem = {
				createdAt: Date.now(),
				parentVersion: 1,
				version: 0,
			};
			this.document.useGlobal = false;
			this.document.parentVersion = 1;
			this.document.version = 0;
			this.document.isBasedOnCloserTemplate = false;
			this.document.versions = [initiaTemplateVersionItem];
			this.document.region = 'ca-on';
			this.initialVersion = initiaTemplateVersionItem;
			this.versionList = [{ name: '1.0.0', value: initiaTemplateVersionItem }];
		} else if (data.mode === EditDocumentDrawerModeEnum.EDIT) {
			const mergedVersionList = getTemplateVersionList(
				data.globalDocument,
				data.document,
				true,
			);
			this.initialVersion = mergedVersionList.find(
				(mergedVersion) =>
					mergedVersion.versionItem.version === this.document.version &&
					mergedVersion.versionItem.parentVersion ===
						this.document.parentVersion,
			)?.versionItem;
			this.versionList = mergedVersionList.map((ver) => {
				return {
					name: getDocumentVersion(ver.versionItem),
					value: ver.versionItem,
				};
			});
		} else {
			throw new Error('Invalid mode');
		}

		if (
			!data?.baseDocxs?.find((baseDocx) => baseDocx.id === 'default_base_docx')
		) {
			this.baseDocxList.push({
				name: 'Default Base Docx',
				value: 'default_base_docx',
			});
		} else if (data.baseDocxs?.length > 0) {
			this.baseDocxList.push(
				...data.baseDocxs.map((baseDocx) => {
					return {
						name: baseDocx.title,
						value: baseDocx.id,
					};
				}),
			);
		}
	}

	ngOnInit(): void {
		this.drawerService.setPrimaryAction('Save', () => this.save());
		this.drawerService.setSecondaryAction('Cancel', () => {
			this.drawerService.closeDrawer();
		});
	}

	async save() {
		try {
			this.form.onSubmit(null);
			const form = this.form;
			if (!form.valid) {
				return;
			}

			await this.updateDocument();

			const documentStyles = this.getDocumentStyles();

			// Handle document file operations based on mode
			if (
				this.data.mode === EditDocumentDrawerModeEnum.EDIT &&
				this.hasVersionChanged
			) {
				await this.handleEditMode();
			} else if (this.data.mode === EditDocumentDrawerModeEnum.CREATE) {
				await this.handleCreateMode(documentStyles);
			}

			this.drawerService.closeDrawer();
		} catch (error) {
			console.error('Error saving document:', error);
		}
	}

	private async updateDocument(): Promise<void> {
		const docSource =
			this.mode === EditDocumentDrawerModeEnum.CREATE
				? createId(7)
				: this.document.source;

		this.document.source = docSource;
		this.document.businessId = this.businessId;
		this.document.id = getDocumentNonHashedId(this.document);
		await this.globalDocxTemplatesRepo.Put(this.document.id, this.document);
	}

	private getDocumentStyles(): WordDocumentStyles | null {
		const defaultStyles = { ...this.wordDocumentStyles };
		return this.document?.ignoreBusinessStylingSettings ? null : defaultStyles;
	}

	private async handleEditMode(): Promise<void> {
		this.updateDocument();
	}

	private async handleCreateMode(
		documentStyles: WordDocumentStyles | null,
	): Promise<void> {
		const storageRef = getBusinessTemplateStorageRefByDocument(this.document);
		const copyTemplateRequest: CopyTemplateParams = {
			sourcePath: 'word-documents/global/blank.docx',
			destinationPath: storageRef,
			title: this.document.title,
			parameters: this.getDefaultParameters(),
			wordDocumentStyles: documentStyles,
			baseDocumentPath: null,
		};

		await this.docxDocumentService
			.copyTemplate(copyTemplateRequest)
			.pipe(take(1))
			.toPromise();
		await this.webdavService.openDocumentForEditing(
			storageRef,
			this.businessId,
			null,
			this.document.id,
			this.fileNameService.getFileName(this.document.title),
		);
	}

	onVersionSelected(versionItem: BusinessDocumentVersionItem) {
		const { parentVersion, version } = versionItem;

		// If version is the same as current then do nothing
		if (
			parentVersion === this.document.parentVersion &&
			version === this.document.version
		)
			return;

		// Once you point the a different version, you will not longer be pointing to closer global
		this.document.useGlobal = false;
		this.document.parentVersion = parentVersion;
		this.document.version = version;

		this.hasVersionChanged = true;
	}

	onMatterTypesUpdate(matterTypes: MatterFileType[]) {
		this.document.matterType = matterTypes;
	}

	private getDefaultParameters() {
		return {
			Context: null,
			MatterId: null,
			DocumentType: DocumentType.BUSINESS_TEMPLATE as string,
		};
	}
}
